import React, { useEffect, useState } from 'react';
import {
  Divider,
  Dropdown,
  Form,
  Grid,
  Icon,
  Input,
  Message,
  Modal,
  Table,
  TextArea
} from 'semantic-ui-react';

import { CustomDropDownOptions } from '../../../types';
import Button from '../../../components/Button';
import { FormatCurrency, UnMaskCurrency } from '../../../util';
import { maskCpf, maskCnpj, maskPhoneNumber } from '../../../services/masks';
import { getEstate } from '../../../services/estate';
import {
  listStandaloneChargeCommissionCollaborators,
  StandaloneChargeCommissionCollaboratorDTO
} from '../../../services/standalone-charges';
import { dangerNotification } from '../../../services/notification';

import { CreateFormErrors, CreateFormState } from '../types';

type Props = {
  open: boolean;
  onClose: () => void;
  isEstateUser: boolean;
  estateOptions: Array<CustomDropDownOptions>;
  defaultEstateId: string;
  form: CreateFormState;
  errors: CreateFormErrors;
  saving: boolean;
  onChangeField: <K extends keyof CreateFormState>(
    field: K,
    value: CreateFormState[K]
  ) => void;
  onReview: () => boolean;
  onSubmit: () => void;
  addressLookupLoading: boolean;
  addressLookupError: string | null;
  addressAutoFilled: string[];
  onPayerAddressZipCodeInput: (value: string) => void;
};

const CreateChargeModal: React.FC<Props> = ({
  open,
  onClose,
  isEstateUser,
  estateOptions,
  defaultEstateId,
  form,
  errors,
  saving,
  onChangeField,
  onReview,
  onSubmit,
  addressLookupLoading,
  addressLookupError,
  addressAutoFilled,
  onPayerAddressZipCodeInput
}) => {
  const [collaboratorOptions, setCollaboratorOptions] = useState<
    Array<{ key: string; text: string; value: string }>
  >([]);
  const [collaboratorsLoading, setCollaboratorsLoading] = useState(false);
  const [hasCommission, setHasCommission] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [estateName, setEstateName] = useState('');
  const [operationFeePercent, setOperationFeePercent] = useState(0);
  const [commissionPercent, setCommissionPercent] = useState(0);

  const selectedEstateId = isEstateUser ? defaultEstateId : form.estateId;
  const selectedCollaborators = collaboratorOptions.filter(option =>
    form.commissionCollaboratorIds.includes(option.value)
  );
  const hasSelectedCollaborators = hasCommission && selectedCollaborators.length > 0;
  const maxContentHeight = reviewing
    ? 'min(78vh, 620px)'
    : hasCommission
    ? 'min(80vh, 750px)'
    : 'min(72vh, 560px)';
  const roundedAmount = Number((form.amount || 0).toFixed(2));
  const operationFeeValue = Number(
    ((roundedAmount * Math.max(operationFeePercent, 0)) / 100).toFixed(2)
  );
  const netAmount = Number(Math.max(roundedAmount - operationFeeValue, 0).toFixed(2));
  const configuredCollaboratorPercent = hasSelectedCollaborators
    ? Math.max(commissionPercent, 0)
    : 0;
  const collaboratorTotalPercent = Math.min(
    configuredCollaboratorPercent * selectedCollaborators.length,
    100
  );
  const collaboratorPercent = hasSelectedCollaborators
    ? collaboratorTotalPercent / selectedCollaborators.length
    : 0;
  const estatePercent = Number((100 - collaboratorTotalPercent).toFixed(2));
  const estateAmount = Number(((netAmount * estatePercent) / 100).toFixed(2));
  const collaboratorAmount = Number(((netAmount * collaboratorPercent) / 100).toFixed(2));
  const totalDistributed = Number(
    (estateAmount + collaboratorAmount * selectedCollaborators.length).toFixed(2)
  );

  const formatMoney = (value: number) => `R$ ${FormatCurrency(value)}`;
  const formatPercent = (value: number) =>
    `${value.toLocaleString('pt-BR', {
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    })}%`;

  const resetReviewAndClose = () => {
    setReviewing(false);
    onClose();
  };

  const goToReview = async () => {
    if (!onReview()) return;

    setReviewLoading(true);
    try {
      const estate = await getEstate(selectedEstateId);
      const commissionEnabled = Boolean(estate.standalone_charges_commission_enabled);

      setHasCommission(commissionEnabled);
      setEstateName(estate.name || 'Nome da imobiliária');
      setOperationFeePercent(Number(estate.standalone_charges_split_percent || 0));
      setCommissionPercent(Number(estate.standalone_charges_commission_percent || 0));

      if (!commissionEnabled && form.commissionCollaboratorIds.length) {
        onChangeField('commissionCollaboratorIds', [] as any);
      }

      setReviewing(true);
    } catch (error) {
      dangerNotification(
        'Erro',
        'Não foi possível verificar o comissionamento da imobiliária.'
      );
    } finally {
      setReviewLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !selectedEstateId) {
      setHasCommission(false);
      setCollaboratorOptions([]);
      setEstateName('');
      setOperationFeePercent(0);
      setCommissionPercent(0);
      setReviewing(false);
      if (form.commissionCollaboratorIds.length) {
        onChangeField('commissionCollaboratorIds', [] as any);
      }
      return;
    }

    const loadCommissionFlag = async () => {
      try {
        const estate = await getEstate(selectedEstateId);
        const commissionEnabled = Boolean(estate.standalone_charges_commission_enabled);

        setHasCommission(commissionEnabled);
        setEstateName('');
        setOperationFeePercent(0);
        setCommissionPercent(0);
        setReviewing(false);

        if (!commissionEnabled && form.commissionCollaboratorIds.length) {
          onChangeField('commissionCollaboratorIds', [] as any);
        }
      } catch (error) {
        setHasCommission(false);
        setCollaboratorOptions([]);
        setEstateName('');
        setOperationFeePercent(0);
        setCommissionPercent(0);
        setReviewing(false);
        if (form.commissionCollaboratorIds.length) {
          onChangeField('commissionCollaboratorIds', [] as any);
        }
      }
    };

    loadCommissionFlag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedEstateId]);

  useEffect(() => {
    if (!open || !hasCommission || !selectedEstateId) {
      setCollaboratorOptions([]);
      return;
    }

    const loadCollaborators = async () => {
      setCollaboratorsLoading(true);
      try {
        const response = await listStandaloneChargeCommissionCollaborators({
          estateId: selectedEstateId,
          status: 'ACTIVE',
          limit: 100
        });
        const options = (response.data || []).map(
          (collaborator: StandaloneChargeCommissionCollaboratorDTO) => ({
            key: collaborator.id,
            value: collaborator.id,
            text: collaborator.user?.name || collaborator.user?.doc || 'Colaborador sem nome'
          })
        );
        setCollaboratorOptions(options);
      } catch (error) {
        setCollaboratorOptions([]);
        dangerNotification('Erro', 'Não foi possível carregar colaboradores da imobiliária.');
      } finally {
        setCollaboratorsLoading(false);
      }
    };

    loadCollaborators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCommission, open, selectedEstateId]);

  useEffect(() => {
    if (!open) {
      setReviewing(false);
    }
  }, [open]);

  useEffect(() => {
    setReviewing(false);
  }, [form.amount, form.commissionCollaboratorIds, form.estateId, form.paymentMethod]);

  return (
    <Modal
      open={open}
      onClose={resetReviewAndClose}
      size="small"
      closeIcon
      style={{ maxHeight: '95vh' }}
    >
      <Modal.Header style={{ flexShrink: 0 }}>
        <Icon name="tag" />
        Nova cobrança
      </Modal.Header>
      <Modal.Content
        style={{
          maxHeight: maxContentHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box' as const
        }}
      >
        {reviewing ? (
          <div>
            <h2
              style={{
                fontSize: 16,
                color: '#2B313C',
                borderBottom: '1px solid #DDDDDD',
                paddingBottom: 12,
                margin: '8px 0 12px',
                textTransform: 'uppercase'
              }}
            >
              Resumo da cobrança
            </h2>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 16,
                marginBottom: 12
              }}
            >
              <div
                style={{
                  border: '1px solid #DDDDDD',
                  borderRadius: 6,
                  padding: '12px 14px'
                }}
              >
                <div style={{ color: '#767676', fontSize: 12, marginBottom: 8 }}>
                  Valor da cobrança
                </div>
                <strong style={{ color: '#2B313C', fontSize: 16 }}>
                  {formatMoney(roundedAmount)}
                </strong>
              </div>
              <div
                style={{
                  border: '1px solid #DDDDDD',
                  borderRadius: 6,
                  padding: '12px 14px'
                }}
              >
                <div style={{ color: '#767676', fontSize: 12, marginBottom: 8 }}>
                  Taxa de operação
                </div>
                <strong style={{ color: '#2B313C', fontSize: 16 }}>
                  {formatPercent(operationFeePercent)}
                </strong>{' '}
                <span style={{ color: '#2B313C', fontSize: 13 }}>
                  ({formatMoney(operationFeeValue)})
                </span>
              </div>
            </div>

            <div
              style={{
                border: '1px solid #DDDDDD',
                borderRadius: 6,
                padding: '12px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12
              }}
            >
              <span style={{ color: '#767676', fontSize: 13 }}>
                Valor líquido a distribuir
              </span>
              <strong style={{ color: '#2B313C', fontSize: 16 }}>
                {formatMoney(netAmount)}
              </strong>
            </div>

            <h3 style={{ color: '#2B313C', fontSize: 15, margin: '0 0 12px' }}>
              <Icon name="angle right" />
              Repasse
            </h3>

            {hasSelectedCollaborators && (
              <div
                style={{
                  border: '1px solid #DDDDDD',
                  borderRadius: 6,
                  padding: '12px 14px',
                  marginBottom: 12,
                  background: '#FFFFFF'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: '#767676',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginBottom: 8
                  }}
                >
                  <span>Divisão do valor líquido</span>
                  <span>
                    {selectedCollaborators.length}{' '}
                    {selectedCollaborators.length === 1 ? 'colaborador' : 'colaboradores'} -{' '}
                    <span style={{ color: '#7D45E8' }}>
                      {formatPercent(collaboratorPercent)} cada
                    </span>
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    height: 8,
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: '#E5E7EB',
                    marginBottom: 8
                  }}
                >
                  <div
                    style={{
                      width: `${estatePercent}%`,
                      background: '#6B7280'
                    }}
                  />
                  <div
                    style={{
                      width: `${collaboratorTotalPercent}%`,
                      background: '#8B5CF6'
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: '#767676',
                    fontSize: 12
                  }}
                >
                  <span>
                    <Icon name="circle" style={{ color: '#6B7280', fontSize: 8 }} />
                    Imobiliária{' '}
                    <strong style={{ color: '#6B7280' }}>{formatPercent(estatePercent)}</strong>
                  </span>
                  <span>
                    <Icon name="circle" style={{ color: '#8B5CF6', fontSize: 8 }} />
                    Colaboradores{' '}
                    <strong style={{ color: '#8B5CF6' }}>
                      {formatPercent(collaboratorTotalPercent)}
                    </strong>
                  </span>
                </div>
              </div>
            )}

            <Table celled compact="very" style={{ borderRadius: 6, overflow: 'hidden' }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ color: '#767676', fontSize: 12 }}>
                    DESTINATÁRIO
                  </Table.HeaderCell>
                  {hasSelectedCollaborators && (
                    <Table.HeaderCell
                      textAlign="center"
                      style={{ color: '#767676', fontSize: 12, width: 100 }}
                    >
                      %
                    </Table.HeaderCell>
                  )}
                  <Table.HeaderCell
                    textAlign="right"
                    style={{ color: '#767676', fontSize: 12, width: 140 }}
                  >
                    VALOR
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>{estateName || 'Nome da imobiliária'}</Table.Cell>
                  {hasSelectedCollaborators && (
                    <Table.Cell textAlign="center">
                      <span
                        style={{
                          background: '#EEF0F3',
                          borderRadius: 4,
                          padding: '3px 6px',
                          color: '#6B7280',
                          fontSize: 12
                        }}
                      >
                        {formatPercent(estatePercent)}
                      </span>
                    </Table.Cell>
                  )}
                  <Table.Cell textAlign="right">{formatMoney(estateAmount)}</Table.Cell>
                </Table.Row>
                {selectedCollaborators.map(collaborator => (
                  <Table.Row key={collaborator.value}>
                    <Table.Cell>{collaborator.text}</Table.Cell>
                    <Table.Cell textAlign="center">
                      <span
                        style={{
                          background: '#EDE7FF',
                          borderRadius: 4,
                          padding: '3px 6px',
                          color: '#7D45E8',
                          fontSize: 12
                        }}
                      >
                        {formatPercent(collaboratorPercent)}
                      </span>
                    </Table.Cell>
                    <Table.Cell textAlign="right">{formatMoney(collaboratorAmount)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
              <Table.Footer>
                <Table.Row>
                  <Table.HeaderCell
                    colSpan={hasSelectedCollaborators ? 2 : 1}
                    style={{ color: '#2B313C' }}
                  >
                    Total repassado
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="right" style={{ color: '#2B313C' }}>
                    {formatMoney(totalDistributed)}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Footer>
            </Table>
          </div>
        ) : (
        <Form>
          <Grid>
            {!isEstateUser && (
              <Grid.Row columns={1}>
                <Grid.Column computer={16} tablet={16} mobile={16}>
                  <Form.Field error={Boolean(errors.estateId)}>
                    <label>Imobiliária *</label>
                    <Dropdown
                      id="create-estate-dropdown"
                      fluid
                      selection
                      search
                      clearable
                      placeholder="Selecione a imobiliária"
                      options={estateOptions}
                      value={form.estateId}
                      onChange={(_, { value }) =>
                        onChangeField('estateId', ((value as string) || '') as any)
                      }
                    />
                    {errors.estateId && (
                      <span
                        id="create-estate-error"
                        style={{ color: '#9f3a38', fontSize: 12 }}
                      >
                        {errors.estateId}
                      </span>
                    )}
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            )}

            <Grid.Row columns={3}>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.paymentMethod)}>
                  <label>Método de pagamento *</label>
                  <Dropdown
                    id="create-payment-method-input"
                    fluid
                    selection
                    options={[
                      { key: 'PIX', value: 'PIX', text: 'PIX' },
                      { key: 'BOLETO', value: 'BOLETO', text: 'Boleto' }
                    ]}
                    value={form.paymentMethod}
                    onChange={(_, { value }) =>
                      onChangeField('paymentMethod', String(value || 'PIX') as any)
                    }
                  />
                  {errors.paymentMethod && (
                    <span style={{ color: '#9f3a38', fontSize: 12 }}>
                      {errors.paymentMethod}
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.amount)}>
                  <label>Valor da cobrança *</label>
                  <Input
                    id="create-amount-input"
                    placeholder="0,00"
                    value={form.amount ? FormatCurrency(form.amount) : ''}
                    aria-invalid={Boolean(errors.amount)}
                    aria-describedby={errors.amount ? 'create-amount-error' : undefined}
                    onChange={(_, { value }) =>
                      onChangeField('amount', UnMaskCurrency(value) as any)
                    }
                  />
                  {errors.amount ? (
                    <span
                      id="create-amount-error"
                      style={{ color: '#9f3a38', fontSize: 12 }}
                    >
                      {errors.amount}
                    </span>
                  ) : (
                    <span style={{ color: '#767676', fontSize: 12 }}>
                      Informe o valor total da cobrança (ex: 150,00).
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.payerType)}>
                  <label>Tipo de pagador *</label>
                  <Dropdown
                    id="create-payer-type-input"
                    fluid
                    selection
                    options={[
                      { key: 'individual', value: 'individual', text: 'Pessoa física (CPF)' },
                      { key: 'company', value: 'company', text: 'Pessoa jurídica (CNPJ)' }
                    ]}
                    value={form.payerType}
                    onChange={(_, { value }) =>
                      onChangeField('payerType', String(value || 'individual') as any)
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Field error={Boolean(errors.payerCpf)}>
                  <label>
                    {form.payerType === 'company' ? 'CNPJ do pagador *' : 'CPF do pagador *'}
                  </label>
                  <Input
                    id="create-payer-cpf-input"
                    placeholder={
                      form.payerType === 'company' ? '00.000.000/0000-00' : '000.000.000-00'
                    }
                    value={form.payerCpf}
                    aria-invalid={Boolean(errors.payerCpf)}
                    aria-describedby={errors.payerCpf ? 'create-cpf-error' : undefined}
                    onChange={(_, { value }) =>
                      onChangeField(
                        'payerCpf',
                        (form.payerType === 'company' ? maskCnpj(value) : maskCpf(value)) as any
                      )
                    }
                  />
                  {errors.payerCpf ? (
                    <span
                      id="create-cpf-error"
                      style={{ color: '#9f3a38', fontSize: 12 }}
                    >
                      {errors.payerCpf}
                    </span>
                  ) : (
                    <span style={{ color: '#767676', fontSize: 12 }}>
                      {form.payerType === 'company'
                        ? 'Informe o CNPJ da empresa pagadora.'
                        : 'Informe o CPF do pagador.'}
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={5} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.payerPhone)}>
                  <label>Telefone do pagador *</label>
                  <Input
                    id="create-payer-phone-input"
                    placeholder="(00) 00000-0000"
                    value={form.payerPhone}
                    aria-invalid={Boolean(errors.payerPhone)}
                    aria-describedby={
                      errors.payerPhone ? 'create-phone-error' : undefined
                    }
                    onChange={(_, { value }) =>
                      onChangeField('payerPhone', maskPhoneNumber(value) as any)
                    }
                  />
                  {errors.payerPhone && (
                    <span
                      id="create-phone-error"
                      style={{ color: '#9f3a38', fontSize: 12 }}
                    >
                      {errors.payerPhone}
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={2}>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.payerName)}>
                  <label>
                    {form.payerType === 'company' ? 'Razão social do pagador *' : 'Nome do pagador *'}
                  </label>
                  <Input
                    id="create-payer-name-input"
                    value={form.payerName}
                    aria-invalid={Boolean(errors.payerName)}
                    aria-describedby={errors.payerName ? 'create-name-error' : undefined}
                    onChange={(_, { value }) => onChangeField('payerName', value as any)}
                  />
                  {errors.payerName && (
                    <span
                      id="create-name-error"
                      style={{ color: '#9f3a38', fontSize: 12 }}
                    >
                      {errors.payerName}
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.payerEmail)}>
                  <label>E-mail da cobrança *</label>
                  <Input
                    id="create-payer-email-input"
                    value={form.payerEmail}
                    aria-invalid={Boolean(errors.payerEmail)}
                    aria-describedby={
                      errors.payerEmail ? 'create-email-error' : undefined
                    }
                    onChange={(_, { value }) => onChangeField('payerEmail', value as any)}
                  />
                  {errors.payerEmail && (
                    <span
                      id="create-email-error"
                      style={{ color: '#9f3a38', fontSize: 12 }}
                    >
                      {errors.payerEmail}
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            {form.paymentMethod === 'BOLETO' && (
              <>
                <Grid.Row columns={1}>
                  <Grid.Column computer={16} tablet={16} mobile={16}>
                    <Divider horizontal>Endereço do pagador</Divider>
                    <span style={{ color: '#767676', fontSize: 12 }}>
                      Obrigatório para emissão de boleto pela Pagar.me. Digite o CEP para
                      preencher o endereço.
                    </span>
                    {addressLookupError && (
                      <Message negative size="small" style={{ marginTop: 8 }}>
                        {addressLookupError}
                      </Message>
                    )}
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={3}>
                  <Grid.Column computer={6} tablet={8} mobile={16}>
                    <Form.Field error={Boolean(errors.payerAddressZipCode)}>
                      <label>CEP *</label>
                      <Input
                        id="create-payer-address-zip-code-input"
                        placeholder="00000-000"
                        value={form.payerAddressZipCode}
                        loading={addressLookupLoading}
                        aria-invalid={Boolean(errors.payerAddressZipCode)}
                        aria-describedby={
                          errors.payerAddressZipCode
                            ? 'create-payer-address-zip-code-error'
                            : undefined
                        }
                        onChange={(_, { value }) => onPayerAddressZipCodeInput(value)}
                      />
                      {errors.payerAddressZipCode && (
                        <span
                          id="create-payer-address-zip-code-error"
                          style={{ color: '#9f3a38', fontSize: 12 }}
                        >
                          {errors.payerAddressZipCode}
                        </span>
                      )}
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column computer={7} tablet={8} mobile={16}>
                    <Form.Field error={Boolean(errors.payerAddressStreet)}>
                      <label>Rua *</label>
                      <Input
                        id="create-payer-address-street-input"
                        value={form.payerAddressStreet}
                        disabled={addressAutoFilled.includes('payerAddressStreet')}
                        aria-invalid={Boolean(errors.payerAddressStreet)}
                        aria-describedby={
                          errors.payerAddressStreet
                            ? 'create-payer-address-street-error'
                            : undefined
                        }
                        onChange={(_, { value }) =>
                          onChangeField('payerAddressStreet', value as any)
                        }
                      />
                      {errors.payerAddressStreet && (
                        <span
                          id="create-payer-address-street-error"
                          style={{ color: '#9f3a38', fontSize: 12 }}
                        >
                          {errors.payerAddressStreet}
                        </span>
                      )}
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={8} mobile={16}>
                    <Form.Field error={Boolean(errors.payerAddressNumber)}>
                      <label>Número *</label>
                      <Input
                        id="create-payer-address-number-input"
                        value={form.payerAddressNumber}
                        aria-invalid={Boolean(errors.payerAddressNumber)}
                        aria-describedby={
                          errors.payerAddressNumber
                            ? 'create-payer-address-number-error'
                            : undefined
                        }
                        onChange={(_, { value }) =>
                          onChangeField('payerAddressNumber', value as any)
                        }
                      />
                      {errors.payerAddressNumber && (
                        <span
                          id="create-payer-address-number-error"
                          style={{ color: '#9f3a38', fontSize: 12 }}
                        >
                          {errors.payerAddressNumber}
                        </span>
                      )}
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={4}>
                  <Grid.Column computer={5} tablet={8} mobile={16}>
                    <Form.Field error={Boolean(errors.payerAddressNeighborhood)}>
                      <label>Bairro *</label>
                      <Input
                        id="create-payer-address-neighborhood-input"
                        value={form.payerAddressNeighborhood}
                        aria-invalid={Boolean(errors.payerAddressNeighborhood)}
                        aria-describedby={
                          errors.payerAddressNeighborhood
                            ? 'create-payer-address-neighborhood-error'
                            : undefined
                        }
                        onChange={(_, { value }) =>
                          onChangeField('payerAddressNeighborhood', value as any)
                        }
                        disabled={addressAutoFilled.includes('payerAddressNeighborhood')}
                      />
                      {errors.payerAddressNeighborhood && (
                        <span
                          id="create-payer-address-neighborhood-error"
                          style={{ color: '#9f3a38', fontSize: 12 }}
                        >
                          {errors.payerAddressNeighborhood}
                        </span>
                      )}
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column computer={5} tablet={8} mobile={16}>
                    <Form.Field error={Boolean(errors.payerAddressCity)}>
                      <label>Cidade *</label>
                      <Input
                        id="create-payer-address-city-input"
                        value={form.payerAddressCity}
                        aria-invalid={Boolean(errors.payerAddressCity)}
                        aria-describedby={
                          errors.payerAddressCity
                            ? 'create-payer-address-city-error'
                            : undefined
                        }
                        onChange={(_, { value }) =>
                          onChangeField('payerAddressCity', value as any)
                        }
                        disabled={addressAutoFilled.includes('payerAddressCity')}
                      />
                      {errors.payerAddressCity && (
                        <span
                          id="create-payer-address-city-error"
                          style={{ color: '#9f3a38', fontSize: 12 }}
                        >
                          {errors.payerAddressCity}
                        </span>
                      )}
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column computer={2} tablet={8} mobile={16}>
                    <Form.Field error={Boolean(errors.payerAddressState)}>
                      <label>UF *</label>
                      <Input
                        id="create-payer-address-state-input"
                        maxLength={2}
                        value={form.payerAddressState}
                        aria-invalid={Boolean(errors.payerAddressState)}
                        aria-describedby={
                          errors.payerAddressState
                            ? 'create-payer-address-state-error'
                            : undefined
                        }
                        onChange={(_, { value }) =>
                          onChangeField('payerAddressState', value.toUpperCase() as any)
                        }
                        disabled={addressAutoFilled.includes('payerAddressState')}
                      />
                      {errors.payerAddressState && (
                        <span
                          id="create-payer-address-state-error"
                          style={{ color: '#9f3a38', fontSize: 12 }}
                        >
                          {errors.payerAddressState}
                        </span>
                      )}
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column computer={4} tablet={8} mobile={16}>
                    <Form.Field error={Boolean(errors.payerAddressComplement)}>
                      <label>Complemento</label>
                      <Input
                        id="create-payer-address-complement-input"
                        value={form.payerAddressComplement}
                        aria-invalid={Boolean(errors.payerAddressComplement)}
                        onChange={(_, { value }) =>
                          onChangeField('payerAddressComplement', value as any)
                        }
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </>
            )}

            <Grid.Row columns={1}>
              <Grid.Column computer={16} tablet={16} mobile={16}>
                <Form.Field error={Boolean(errors.description)}>
                  <label>Descrição da cobrança *</label>
                  <TextArea
                    id="create-description-input"
                    value={form.description}
                    aria-invalid={Boolean(errors.description)}
                    aria-describedby={
                      errors.description ? 'create-description-error' : undefined
                    }
                    onChange={(_, { value }) =>
                      onChangeField('description', String(value) as any)
                    }
                  />
                  {errors.description && (
                    <span
                      id="create-description-error"
                      style={{ color: '#9f3a38', fontSize: 12 }}
                    >
                      {errors.description}
                    </span>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            {hasCommission && (
              <>
                <Grid.Row columns={1}>
                  <Grid.Column computer={16} tablet={16} mobile={16}>
                    <h2
                      style={{
                        fontSize: '16px',
                        borderBottom: '1px solid #DDDDDD',
                        letterSpacing: '0%',
                        color: '#2B313C',
                        paddingBottom: '10px'
                      }}
                    >
                      COMISSIONAMENTO
                    </h2>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={1} style={{ marginTop: 0 }}>
                  <Grid.Column computer={16} tablet={16} mobile={16}>
                    <Form.Field error={Boolean(errors.estateId)}>
                      <label>Adicionar colaborador</label>
                      <Dropdown
                        id="create-commission-collaborators-dropdown"
                        fluid
                        multiple
                        selection
                        search
                        placeholder="Selecione"
                        options={collaboratorOptions}
                        value={form.commissionCollaboratorIds}
                        loading={collaboratorsLoading}
                        disabled={collaboratorsLoading || !selectedEstateId}
                        onChange={(_, { value }) =>
                          onChangeField(
                            'commissionCollaboratorIds',
                            ((value as string[]) || []) as any
                          )
                        }
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
          </Grid>
        </Form>
        )}
      </Modal.Content>
      <Modal.Actions style={{ flexShrink: 0 }}>
        {reviewing ? (
          <>
            <Button
              type="button"
              text="Voltar"
              light
              disabled={saving}
              onClick={() => setReviewing(false)}
            />
            <Button
              type="button"
              text="Confirmar"
              loading={saving}
              disabled={saving}
              onClick={onSubmit}
            />
          </>
        ) : (
          <>
            <Button type="button" text="Cancelar" light onClick={resetReviewAndClose} />
            <Button
              type="button"
              text="Avançar"
              loading={saving || reviewLoading}
              disabled={saving || reviewLoading}
              onClick={goToReview}
            />
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default CreateChargeModal;
